import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Trash2, Database, RefreshCw, HardDrive, ArrowRightCircle } from 'lucide-react';
import { cleanupDuplicates, getDocumentSize, migrateInventoryToCollection, checkMigrationNeeded } from '../utils/migrateData';
import { useGroup } from '../contexts/GroupContext';
import toast from 'react-hot-toast';

const DataManagement = ({ onClose }) => {
  const { currentGroup } = useGroup();
  const [loading, setLoading] = useState(false);
  const [docSize, setDocSize] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (currentGroup?.id) {
      checkDataStatus();
    }
  }, [currentGroup?.id]);

  const checkDataStatus = async () => {
    setLoading(true);
    try {
      const size = await getDocumentSize(currentGroup.id);
      setDocSize(size);
      
      const migration = await checkMigrationNeeded(currentGroup.id);
      setMigrationStatus(migration);
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Failed to check data status');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!currentGroup?.id) return;
    
    setLoading(true);
    try {
      const result = await cleanupDuplicates(currentGroup.id);
      
      if (result.success) {
        toast.success(result.message);
        await checkDataStatus(); // Refresh stats
        
        if (result.removedCount > 0) {
          // Reload the page to get fresh data
          setTimeout(() => window.location.reload(), 1500);
        }
      } else {
        toast.error(result.error || 'Failed to cleanup');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to cleanup duplicates');
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    if (!currentGroup?.id) return;
    
    const confirmed = window.confirm(
      'This will migrate your inventory to a new structure. This is recommended if your data is getting large. Continue?'
    );
    
    if (!confirmed) return;
    
    setLoading(true);
    try {
      const result = await migrateInventoryToCollection(currentGroup.id, (p) => {
        setProgress(p);
      });
      
      if (result.success) {
        toast.success(result.message);
        await checkDataStatus();
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(result.error || 'Migration failed');
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('Migration failed');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
              <Database className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Data Management</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Clean up and optimize your data</p>
            </div>
          </div>

          {/* Document Size */}
          {docSize && (
            <div className={`p-4 rounded-xl mb-4 ${
              docSize.isOverLimit 
                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
                : docSize.percentOfLimit > 70
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <HardDrive size={18} className={
                  docSize.isOverLimit 
                    ? 'text-red-600' 
                    : docSize.percentOfLimit > 70 
                      ? 'text-amber-600' 
                      : 'text-green-600'
                } />
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  Document Size: {docSize.formatted}
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full ${
                    docSize.isOverLimit 
                      ? 'bg-red-500' 
                      : docSize.percentOfLimit > 70 
                        ? 'bg-amber-500' 
                        : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(parseFloat(docSize.percentOfLimit), 100)}%` }}
                />
              </div>
              
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {docSize.percentOfLimit}% of 1 MB limit
              </p>
              
              {docSize.isOverLimit && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
                  <AlertTriangle size={14} />
                  Over limit! Clean up required.
                </p>
              )}

              {/* Breakdown */}
              {docSize.breakdown && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Size Breakdown:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Shows: {(docSize.breakdown.shows / 1024).toFixed(1)} KB</div>
                    <div>Inventory: {(docSize.breakdown.inventory / 1024).toFixed(1)} KB</div>
                    <div>Expenses: {(docSize.breakdown.expenses / 1024).toFixed(1)} KB</div>
                    <div>Contacts: {(docSize.breakdown.contacts / 1024).toFixed(1)} KB</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {/* Cleanup Duplicates */}
            <button
              onClick={handleCleanupDuplicates}
              disabled={loading}
              className="w-full flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50"
            >
              <Trash2 className="text-amber-600 dark:text-amber-400" size={20} />
              <div className="text-left">
                <p className="font-medium text-gray-800 dark:text-gray-100">Remove Duplicates</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Find and remove duplicate inventory items
                </p>
              </div>
            </button>

            {/* Migrate Inventory - only show if not already migrated */}
            {migrationStatus?.needed && (
              <button
                onClick={handleMigrate}
                disabled={loading}
                className="w-full flex items-center gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors disabled:opacity-50"
              >
                <ArrowRightCircle className="text-purple-600 dark:text-purple-400" size={20} />
                <div className="text-left flex-1">
                  <p className="font-medium text-gray-800 dark:text-gray-100">Migrate Inventory</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Move {migrationStatus.inventoryCount} items to optimized storage
                  </p>
                </div>
                <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                  Recommended
                </span>
              </button>
            )}

            {/* Already migrated indicator */}
            {migrationStatus && !migrationStatus.needed && migrationStatus.reason !== 'No data document' && (
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
                <div className="text-left">
                  <p className="font-medium text-gray-800 dark:text-gray-100">Already Optimized</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Inventory is using optimized storage
                  </p>
                </div>
              </div>
            )}

            {/* Refresh Stats */}
            <button
              onClick={checkDataStatus}
              disabled={loading}
              className="w-full flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`text-blue-600 dark:text-blue-400 ${loading ? 'animate-spin' : ''}`} size={20} />
              <div className="text-left">
                <p className="font-medium text-gray-800 dark:text-gray-100">Refresh Stats</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Check current data size and status
                </p>
              </div>
            </button>
          </div>

          {/* Progress indicator */}
          {progress && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {progress.phase === 'starting' && 'Starting migration...'}
                {progress.phase === 'migrating' && `Migrating: ${progress.migrated} / ${progress.total}`}
                {progress.phase === 'cleaning' && 'Cleaning up...'}
                {progress.phase === 'complete' && 'Complete!'}
              </p>
              {progress.total > 0 && (
                <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${(progress.migrated / progress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-full mt-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;